import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DemographicJPFitmentScenario from "../DemographicJPFitmentScenario/DemographicJPFitmentScenario.js";
import {
  SquarePen,
  Save,
  Pencil,
} from "lucide-react";
import {
  getJPFitmentConfiguration,
  updateJPFitmentConfiguration,
} from "../../../../../services/SyncApis";
import { message } from "antd";
import ReusableHeader from "../../../../../reusableField/ReusableHeader";
import BackIcon from "../../../../../Images/backIcon.png";
import HomeIcon from "../../../../../Images/homeIcon.png";
import "./JPFitmentTest.css";
import RealisticIcon from "../../../../../Images/Background+Shadow.png";
import InvestigativeIcon from "../../../../../Images/investigetionIcon.png";
import ArtisticIcon from "../../../../../Images/artisticIcon.png";
import SocialIcon from "../../../../../Images/socialIcon.png";
import EnterprisingIcon from "../../../../../Images/enterprestingIcon.png";
import ConventionalIcon from "../../../../../Images/conventionalIcon.png";


const JPFitmentTest = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const fullState = location.state || {};

  const name = fullState?.name;
  const accountName = fullState?.accountName;
  const roleId = fullState?.roleId;
  const accountId = fullState?.accountId;

  const sections = [
    { name: "Realistic (R)", icon: RealisticIcon },
    { name: "Investigation (I)", icon: InvestigativeIcon },
    { name: "Artistic (A)", icon: ArtisticIcon },
    { name: "Social (S)", icon: SocialIcon },
    { name: "Enterprising (E)", icon: EnterprisingIcon },
    { name: "Conventional (C)", icon: ConventionalIcon },
  ];

  // =====================================================
  // SCORING VALUES
  // =====================================================

  const [scoring, setScoring] = useState({
    primary: "5",
    secondary: "3",
    supporting: "1",
  });

  // Temporary values used while editing
  const [editingScoring, setEditingScoring] = useState({
    primary: "5",
    secondary: "3",
    supporting: "1",
  });

  const [isEditingScoring, setIsEditingScoring] = useState(false);
  const [editingDescription, setEditingDescription] = useState(null);
  const [tempDescription, setTempDescription] = useState("");
  const [loadingConfiguration, setLoadingConfiguration] = useState(false);

  const [sectionDetails, setSectionDetails] = useState({
    "Realistic (R)": {
      description:
        "They are more practical people who like to invest their time working with tangible things like machines, tools etc.",
      weightage: "4",
    },

    "Investigation (I)": {
      description:
        "They are analytical, intellectual and inquisitive people who like to investigate ideas and solve problems.",
      weightage: "4",
    },

    "Artistic (A)": {
      description:
        "They are creative people who like to work with ideas, designs and artistic expression.",
      weightage: "4",
    },

    "Social (S)": {
      description:
        "They are helpful and cooperative people who enjoy working with and supporting others.",
      weightage: "4",
    },

    "Enterprising (E)": {
      description:
        "They are energetic and persuasive people who enjoy leading, influencing and managing others.",
      weightage: "4",
    },

    "Conventional (C)": {
      description:
        "They are organized and detail-oriented people who like structured and systematic activities.",
      weightage: "4",
    },
  });

  // =====================================================
  // LOAD JP FITMENT CONFIGURATION
  // =====================================================

  useEffect(() => {
    if (!roleId || !accountId) {
      return;
    }

    const loadJPFitmentConfiguration = async () => {
      setLoadingConfiguration(true);

      try {
        const response =
          await getJPFitmentConfiguration(
            Number(roleId),
            Number(accountId)
          );

        const data =
          response?.data || response;

        if (!data) {
          throw new Error(
            "JP Fitment configuration not found."
          );
        }

        // =================================================
        // SCORING
        // =================================================

        setScoring({
          primary: String(
            data.primaryTraitScore ?? ""
          ),

          secondary: String(
            data.secondaryTraitScore ?? ""
          ),

          supporting: String(
            data.supportingTraitScore ?? ""
          ),
        });

        // Keep editing values in sync
        setEditingScoring({
          primary: String(
            data.primaryTraitScore ?? ""
          ),

          secondary: String(
            data.secondaryTraitScore ?? ""
          ),

          supporting: String(
            data.supportingTraitScore ?? ""
          ),
        });

        // =================================================
        // RIASEC CATEGORIES
        // =================================================

        if (Array.isArray(data.categories)) {
          setSectionDetails((prev) => {
            const updated = {
              ...prev,
            };

            data.categories.forEach((category) => {
              let sectionName = "";

              switch (category.category) {
                case "R":
                  sectionName = "Realistic (R)";
                  break;

                case "I":
                  sectionName = "Investigation (I)";
                  break;

                case "A":
                  sectionName = "Artistic (A)";
                  break;

                case "S":
                  sectionName = "Social (S)";
                  break;

                case "E":
                  sectionName = "Enterprising (E)";
                  break;

                case "C":
                  sectionName = "Conventional (C)";
                  break;

                default:
                  return;
              }

              if (updated[sectionName]) {
                updated[sectionName] = {
                  ...updated[sectionName],

                  description:
                    category.description ?? "",

                  weightage: String(
                    category.weight ?? 0
                  ),
                };
              }
            });

            return updated;
          });
        }
      } catch (error) {
        console.error(
          "Error fetching JP Fitment configuration:",
          error
        );

        const backendMessage =
          error?.response?.data?.message;

        message.error(
          backendMessage ||
          "Failed to load JP Fitment configuration."
        );
      } finally {
        setLoadingConfiguration(false);
      }
    };

    loadJPFitmentConfiguration();
  }, [roleId, accountId]);

  // =====================================================
  // TAB CHANGE
  // =====================================================

  const handleTabChange = (tab) => {
    if (tab === "demographic") {
      navigate("/assessment-configuration", {
        state: fullState,
      });
    }

    if (tab === "jp-fitment") {
      navigate("/jp-fitment-test", {
        state: fullState,
      });
    }

    if (tab === "scenario-based-assessment") {
      navigate("/scenario-based-assessment", {
        state: fullState,
      });
    }
  };

  // =====================================================
  // EDIT SCORING
  // =====================================================

  const handleEditScoring = () => {
    setEditingScoring({
      primary: scoring.primary,
      secondary: scoring.secondary,
      supporting: scoring.supporting,
    });

    setIsEditingScoring(true);
  };

  // =====================================================
  // NUMBER ONLY INPUT
  // =====================================================

  const handleScoringChange = (field, value) => {
    if (/^\d*$/.test(value)) {
      setEditingScoring((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // =====================================================
  // SAVE SCORING
  // =====================================================

  const handleSaveScoring = async () => {
    if (
      editingScoring.primary === "" ||
      editingScoring.secondary === "" ||
      editingScoring.supporting === ""
    ) {
      return;
    }

    if (!roleId || !accountId) {
      message.error(
        "Role ID or Admin Client ID is missing."
      );
      return;
    }

    const primary = Number(
      editingScoring.primary
    );

    const secondary = Number(
      editingScoring.secondary
    );

    const supporting = Number(
      editingScoring.supporting
    );

    // Backend rule:
    // Primary > Secondary > Supporting
    if (
      !(
        primary >
        secondary &&
        secondary >
        supporting
      )
    ) {
      message.error(
        "Trait scores must satisfy: Primary > Secondary > Supporting."
      );
      return;
    }

    try {
      const response =
        await updateJPFitmentConfiguration(
          Number(roleId),
          Number(accountId),
          {
            primaryTraitScore: primary,
            secondaryTraitScore: secondary,
            supportingTraitScore: supporting,
          }
        );

      const data =
        response?.data || response;

      /*
       * Update UI from the saved backend response.
       */
      if (data) {
        setScoring({
          primary: String(
            data.primaryTraitScore ?? primary
          ),
          secondary: String(
            data.secondaryTraitScore ??
            secondary
          ),
          supporting: String(
            data.supportingTraitScore ??
            supporting
          ),
        });
      } else {
        setScoring({
          primary: String(primary),
          secondary: String(secondary),
          supporting: String(supporting),
        });
      }

      setIsEditingScoring(false);

      message.success(
        "JP Fitment scoring updated successfully."
      );
    } catch (error) {
      console.error(
        "Error updating JP Fitment scoring:",
        error
      );

      const backendMessage =
        error?.response?.data?.message;

      message.error(
        backendMessage ||
        "Failed to update JP Fitment scoring."
      );
    }
  };

  const handleSectionWeightageChange = (
    sectionName,
    value
  ) => {
    setSectionDetails((prev) => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        weightage: value,
      },
    }));
  };

  const getJPFitmentCategoriesPayload = () => {
    const categoryMap = {
      "Realistic (R)": "R",
      "Investigation (I)": "I",
      "Artistic (A)": "A",
      "Social (S)": "S",
      "Enterprising (E)": "E",
      "Conventional (C)": "C",
    };

    return sections.map((section) => ({
      category: categoryMap[section.name],
      weight: Number(
        sectionDetails[section.name]?.weightage ?? 0
      ),
      description:
        sectionDetails[section.name]?.description ?? "",
    }));
  };

  const handleEditDescription = (sectionName) => {
    setTempDescription(
      sectionDetails[sectionName].description
    );

    setEditingDescription(sectionName);
  };

  const handleSaveDescription = async (
    sectionName
  ) => {
    if (!roleId || !accountId) {
      message.error(
        "Role ID or Admin Client ID is missing."
      );
      return;
    }

    const updatedDetails = {
      ...sectionDetails,
      [sectionName]: {
        ...sectionDetails[sectionName],
        description: tempDescription,
      },
    };

    const categories = sections.map((section) => {
      const categoryMap = {
        "Realistic (R)": "R",
        "Investigation (I)": "I",
        "Artistic (A)": "A",
        "Social (S)": "S",
        "Enterprising (E)": "E",
        "Conventional (C)": "C",
      };

      return {
        category: categoryMap[section.name],
        weight: Number(
          updatedDetails[section.name]?.weightage ?? 0
        ),
        description:
          updatedDetails[section.name]?.description ?? "",
      };
    });

    const totalWeight = categories.reduce(
      (sum, item) => sum + item.weight,
      0
    );

    if (totalWeight !== 100) {
      message.error(
        `Category weights must total 100%. Current total: ${totalWeight}%.`
      );
      return;
    }

    try {
      const response =
        await updateJPFitmentConfiguration(
          Number(roleId),
          Number(accountId),
          {
            categories,
          }
        );

      const data =
        response?.data || response;

      /*
       * Update UI with the saved backend response.
       */
      if (Array.isArray(data?.categories)) {
        setSectionDetails((prev) => {
          const updated = {
            ...prev,
          };

          data.categories.forEach((category) => {
            let sectionName = "";

            switch (category.category) {
              case "R":
                sectionName = "Realistic (R)";
                break;

              case "I":
                sectionName = "Investigation (I)";
                break;

              case "A":
                sectionName = "Artistic (A)";
                break;

              case "S":
                sectionName = "Social (S)";
                break;

              case "E":
                sectionName = "Enterprising (E)";
                break;

              case "C":
                sectionName = "Conventional (C)";
                break;

              default:
                return;
            }

            if (updated[sectionName]) {
              updated[sectionName] = {
                ...updated[sectionName],
                description:
                  category.description ?? "",
                weightage: String(
                  category.weight ?? 0
                ),
              };
            }
          });

          return updated;
        });
      }

      setEditingDescription(null);

      message.success(
        "RIASEC configuration updated successfully."
      );
    } catch (error) {
      console.error(
        "Error updating RIASEC configuration:",
        error
      );

      const backendMessage =
        error?.response?.data?.message;

      message.error(
        backendMessage ||
        "Failed to update RIASEC configuration."
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F6F6]">

      <ReusableHeader />

      <div className="px-8 pb-8">

        {/* ===================================================== */}
        {/* BACK / HOME */}
        {/* ===================================================== */}

        <div className="mb-4 -mt-4">
          <div className="flex justify-between items-center w-full mt-5 ml-1">

            {/* Back */}
            <button
              onClick={() =>
                navigate("/sync-configuration", {
                  state: fullState,
                })
              }
              className="
                flex items-center gap-2
                bg-[#C21A17]
                text-white
                px-5 py-2
                rounded-lg
                text-sm
                font-semibold
                shadow-md
                hover:bg-[#A91513]
                transition-all
                duration-200
              "
            >
              <img
                src={BackIcon}
                alt="Back"
                className="w-[15px] h-[15px] object-contain"
              />
              Back
            </button>

            {/* Home */}
            <button
              onClick={() => navigate("/admin")}
              className="
                flex items-center gap-2
                bg-[#C21A17]
                text-white
                px-5 py-2
                rounded-lg
                text-sm
                font-semibold
                shadow-md
                hover:bg-[#A91513]
                transition-all
                duration-200
              "
            >
              <img
                src={HomeIcon}
                alt="Home"
                className="w-[15px] h-[15px] object-contain"
              />
              Home
            </button>

          </div>

          {/* Breadcrumb */}
          <div className="mt-3 ml-2 flex items-center flex-wrap text-[14px] text-[#7A7A7A] gap-1">
            <span
              onClick={() =>
                navigate("/admin")
              }
              className="cursor-pointer"
            >
              {accountName}
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/dashboard", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              {fullState?.name || ""}
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/experience-configuration", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              Experience Configuration
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/productsConfiguration", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              Product Configuration
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              onClick={() =>
                navigate("/sync-configuration", {
                  state: fullState,
                })
              }
              className="cursor-pointer"
            >
              Sync
            </span>

            <span className="mx-1">
              ›
            </span>

            <span
              className="
      font-semibold
      text-[#1B1B1B]
    "
            >
              Assessment Configuration
            </span>

          </div>
        </div>

        <DemographicJPFitmentScenario />

        {/* ===================================================== */}
        {/* SCORING CARD */}
        {/* ===================================================== */}

        <div
          className="
            mt-8
            bg-white
            rounded-[16px]
            shadow-sm
            border
            border-[#EEEEEE]
            p-4
          "
        >

          {/* Scoring Header */}
          <div className="flex justify-between items-center mb-2 px-1">

            <h2 className="text-[22px] font-semibold text-[#111111]">
              Scoring
            </h2>

            {/* EDIT / SAVE BUTTON */}
            {!isEditingScoring ? (
              <button
                type="button"
                onClick={handleEditScoring}
                title="Edit Scoring"
                className="
                  flex
                  items-center
                  justify-center
                  text-[#8A8A8A]
                  hover:text-[#C21A17]
                  transition
                  duration-200
                  cursor-pointer
                "
              >
                <SquarePen
                  size={20}
                  strokeWidth={1.7}
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSaveScoring}
                title="Save Scoring"
                className="
                  h-[32px]
                  px-[16px]
                  rounded-[6px]
                  border
                  border-[#C21A17]
                  bg-white
                  text-[#C21A17]
                  text-[14px]
                  font-medium
                  flex
                  items-center
                  justify-center
                  cursor-pointer
                "
              >
                Save
              </button>
            )}

          </div>

          {/* ===================================================== */}
          {/* SCORE BOXES */}
          {/* ===================================================== */}

          <div className="grid grid-cols-3 gap-8 -mt-4">
            {/* PRIMARY TRAIT */}

            <div
              className="
                h-[51px]
                bg-[#E9F3FF]
                rounded-[12px]
                border
                border-[#D9E8F8]
                flex
                items-center
                justify-between
                px-4
              "
            >
              <span className="text-[14px] text-[#202020]">
                Primary Trait
              </span>

              {!isEditingScoring ? (
                <span className="text-[18px] font-medium text-[#333333]">
                  {scoring.primary}
                </span>
              ) : (
                <input
                  type="text"
                  inputMode="numeric"
                  value={editingScoring.primary}
                  onChange={(e) =>
                    handleScoringChange(
                      "primary",
                      e.target.value
                    )
                  }
                  className="
                    w-[70px]
                    h-[38px]
                    bg-white
                    border
                    border-[#BDBDBD]
                    rounded-[7px]
                    px-2
                    text-right
                    text-[18px]
                    font-semibold
                    text-[#333333]
                    outline-none
                    focus:border-[#C21A17]
                  "
                />
              )}
            </div>

            {/* SECONDARY TRAIT */}

            <div
              className="
                h-[51px]
                bg-[#E9F3FF]
                rounded-[12px]
                border
                border-[#D9E8F8]
                flex
                items-center
                justify-between
                px-4
              "
            >
              <span className="text-[14px] text-[#202020]">
                Secondary Trait
              </span>

              {!isEditingScoring ? (
                <span className="text-[18px] font-medium text-[#333333] ">
                  {scoring.secondary}
                </span>
              ) : (
                <input
                  type="text"
                  inputMode="numeric"
                  value={editingScoring.secondary}
                  onChange={(e) =>
                    handleScoringChange(
                      "secondary",
                      e.target.value
                    )
                  }
                  className="
                    w-[70px]
                    h-[38px]
                    bg-white
                    border
                    border-[#BDBDBD]
                    rounded-[7px]
                    px-2
                    text-right
                    text-[18px]
                    font-semibold
                    text-[#333333]
                    outline-none
                    focus:border-[#C21A17]
                  "
                />
              )}
            </div>

            {/* SUPPORTING TRAIT */}

            <div
              className="
                h-[51px]
                bg-[#E9F3FF]
                rounded-[12px]
                border
                border-[#D9E8F8]
                flex
                items-center
                justify-between
                px-4
              "
            >
              <span className="text-[14px] text-[#202020]">
                Supporting Trait
              </span>

              {!isEditingScoring ? (
                <span className="text-[18px] font-semibold text-[#333333]">
                  {scoring.supporting}
                </span>
              ) : (
                <input
                  type="text"
                  inputMode="numeric"
                  value={editingScoring.supporting}
                  onChange={(e) =>
                    handleScoringChange(
                      "supporting",
                      e.target.value
                    )
                  }
                  className="
                    w-[70px]
                    h-[38px]
                    bg-white
                    border
                    border-[#BDBDBD]
                    rounded-[7px]
                    px-2
                    text-right
                    text-[18px]
                    font-semibold
                    text-[#333333]
                    outline-none
                    focus:border-[#C21A17]
                  "
                />
              )}
            </div>

          </div>

          {/* ===================================================== */}
          {/* SCORING CRITERIA */}
          {/* ===================================================== */}

          <div
            className="
              mt-3
              h-[44px]
              rounded-[14px]
              border
              border-[#E5E5E5]
              flex
              items-center
              px-4
              text-[13px]
              text-[#333333]
              bg-[#FCFCFC]
            "
          >
            <span className="font-semibold mr-1">
              Scoring Criteria:
            </span>

            Points are awarded based on the trait category of the selected
            option:
          </div>

        </div>

        {/* ===================================================== */}
        {/* JP FITMENT TEST */}
        {/* ===================================================== */}

        <div
          className="
            mt-4
            bg-white
            rounded-[16px]
            shadow-sm
            border
            border-[#EEEEEE]
            p-5
            h-[500px]
            overflow-hidden
          "
        >

          {/* Heading */}
          <h2 className="text-[21px] font-semibold text-[#111111]">
            JP Fitment Test
          </h2>

          <p className="mt-1 text-sm text-[#999999]">
            6 Sections
          </p>

          {/* Divider */}
          <div className="mt-3 border-t border-[#BDBDBD]" />

          {/* ===================================================== */}
          {/* SCROLLABLE SIX SECTIONS */}
          {/* ===================================================== */}

          <div
            className="
              mt-4
              space-y-3
              h-[340px]
              overflow-y-auto
              pr-2
            "
          >

            {sections.map((section, index) => {
              const details =
                sectionDetails[section.name];

              return (
                <div
                  key={index}
                  className="
                    rounded-[14px]
                    border
                    border-[#D9D9D9]
                    overflow-hidden
                    bg-white
                  "
                >

                  {/* ================================================= */}
                  {/* SECTION HEADER */}
                  {/* ================================================= */}

                  <div
                    className="
                      w-full
                      min-h-[60px]
                      bg-[#E8F3FF]
                      flex
                      items-center
                      px-5
                    "
                  >

                    {/* LEFT */}

                    <div className="flex items-center gap-3">

                      {/* Icon */}

                      <div
                        className="
                          w-[40px]
                          h-[40px]
                          bg-white
                          rounded-[9px]
                          flex
                          items-center
                          justify-center
                          overflow-hidden
                        "
                      >
                        <img
                          src={section.icon}
                          alt={section.name}
                          className="
                            w-[26px]
                            h-[26px]
                            object-contain
                          "
                        />
                      </div>

                      {/* Name */}

                      <span
                        className="
                          text-[16px]
                          font-semibold
                          text-[#3F4A5C]
                        "
                      >
                        {section.name}
                      </span>

                      {/* Points */}

                      <span
                        className="
                          ml-1
                          bg-[#8DBCF5]
                          text-[#174C83]
                          px-3
                          py-1
                          rounded-[6px]
                          text-[13px]
                          font-semibold
                        "
                      >
                        {details.weightage} points
                      </span>

                    </div>

                  </div>

                  {/* ================================================= */}
                  {/* SECTION CONTENT - ALWAYS VISIBLE */}
                  {/* ================================================= */}

                  <div className="px-5 py-4 bg-white">

                    {/* Description Label */}

                    <div
                      className="
                        text-[16px]
                        font-semibold
                        text-[#333333]
                      "
                    >
                      Description:
                    </div>

                    {/* Description */}

                    <div
                      className="
                        mt-2
                        flex
                        items-start
                        justify-between
                        gap-5
                      "
                    >

                      {editingDescription ===
                        section.name ? (
                        <textarea
                          value={tempDescription}
                          onChange={(e) =>
                            setTempDescription(
                              e.target.value
                            )
                          }
                          className="
                            w-full
                            min-h-[70px]
                            border
                            border-[#D9D9D9]
                            rounded-[8px]
                            px-3
                            py-2
                            text-[16px]
                            text-[#4A5568]
                            leading-7
                            outline-none
                            focus:border-[#C21A17]
                          "
                        />
                      ) : (
                        <p
                          className="
                            text-[16px]
                            text-[#4A5568]
                            leading-7
                            m-0
                          "
                        >
                          {details.description}
                        </p>
                      )}

                      {editingDescription ===
                        section.name ? (
                        <button
                          type="button"
                          onClick={() =>
                            handleSaveDescription(
                              section.name
                            )
                          }
                          title="Save Description"
                          className="
                            flex-shrink-0
                            h-[32px]
                            px-[16px]
                            rounded-[6px]
                            border
                            border-[#C21A17]
                            bg-white
                            text-[#C21A17]
                            text-[14px]
                            font-medium
                            cursor-pointer
                          "
                        >
                          Save
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            handleEditDescription(
                              section.name
                            )
                          }
                          title="Edit Description"
                          className="
                            flex-shrink-0
                            text-[#8A8A8A]
                            hover:text-[#C21A17]
                            transition
                            duration-200
                            cursor-pointer
                          "
                        >
                          <SquarePen
                            size={18}
                            strokeWidth={1.6}
                          />
                        </button>
                      )}

                    </div>

                    {/* Divider */}

                    <div className="mt-3 border-t border-[#E5E5E5]" />

                    {/* Weightage Label */}

                    <div
                      className="
                        mt-3
                        text-sm
                        text-[#333333]
                      "
                    >
                      Weightage (Points)
                      <span className="text-[#C21A17]">
                        *
                      </span>
                    </div>

                    {/* Weightage Input */}

                    <div className="jp-weightage-input mt-2">
                      <input
                        type="number"
                        value={details.weightage}
                        min="0"
                        max="100"
                        step="1"
                        disabled={editingDescription !== section.name}
                        onChange={(e) =>
                          handleSectionWeightageChange(
                            section.name,
                            e.target.value
                          )
                        }
                        className={`
                          w-[483px]
                          h-[44px]
                          border
                          border-[#D9D9D9]
                          rounded-[8px]
                          px-4
                          pr-10
                          text-[14px]
                          text-[#555555]
                          font-normal
                          outline-none
                          focus:ring-0
                          ${editingDescription === section.name
                            ? "bg-white focus:border-[#C21A17] cursor-text"
                            : "bg-[#F5F5F5] cursor-not-allowed"
                          }
                        `}
                      />

                      {editingDescription === section.name && (
                        <div className="jp-weightage-stepper">
                          <button
                            type="button"
                            aria-label={`Increase ${section.name} weightage`}
                            onClick={() =>
                              handleSectionWeightageChange(
                                section.name,
                                String(Math.min(100, Number(details.weightage || 0) + 1))
                              )
                            }
                          >
                            <span className="jp-weightage-arrow jp-weightage-arrow-up" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Decrease ${section.name} weightage`}
                            onClick={() =>
                              handleSectionWeightageChange(
                                section.name,
                                String(Math.max(0, Number(details.weightage || 0) - 1))
                              )
                            }
                          >
                            <span className="jp-weightage-arrow jp-weightage-arrow-down" />
                          </button>
                        </div>
                      )}
                    </div>

                  </div>

                </div>
              );
            })}

          </div>

        </div>

      </div>
    </div>
  );
};

export default JPFitmentTest;